import { useEffect, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
};

const DISMISS_KEY = 'labibur-pwa-install-dismissed';

function isStandaloneDisplay() {
  const navigatorWithStandalone = window.navigator as Navigator & { readonly standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
}

export function PwaInstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isStandaloneDisplay() || window.localStorage.getItem(DISMISS_KEY) === 'true') return undefined;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  if (!isVisible || !installEvent) return null;

  const installApp = async () => {
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      setIsVisible(false);
      setInstallEvent(null);
    }
  };

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, 'true');
    setIsVisible(false);
  };

  return (
    <aside className="pwa-install-prompt" role="dialog" aria-label="Install portfolio app">
      <img src="/pwa/icon-192.png" alt="Labibur App Icon" className="pwa-prompt-icon" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
      <div>
        <strong>Install Labibur</strong>
        <span>Use this portfolio like a mobile app.</span>
      </div>
      <button className="pwa-install-primary" type="button" onClick={installApp}>
        Install
      </button>
      <button className="pwa-install-close" type="button" onClick={dismiss} aria-label="Dismiss install prompt">
        ×
      </button>
    </aside>
  );
}
